import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronDown, Search, X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { normalizeForMatch } from '@/lib/validators'

interface Option {
  value: string
  label: string
}

interface Props {
  label?: string
  error?: string
  options: (Option | string)[]
  placeholder?: string
  /** Texto/hint exibido quando há erro abaixo do campo. */
  hint?: string
  wrapperClassName?: string
  className?: string
  value?: string
  onChange?: (e: { target: { value: string } }) => void
  disabled?: boolean
  required?: boolean
  name?: string
  id?: string
  /** Mostra busca dentro do dropdown (regra global). Default `true`. */
  searchable?: boolean
  /** Mostra X para limpar o valor quando preenchido. Default `true`. */
  clearable?: boolean
  /** Texto do placeholder do campo de busca interno. */
  searchPlaceholder?: string
}

/**
 * Select padrão do sistema — combobox com:
 *  - Painel via React Portal (sobrepõe overflow:hidden de ancestrais)
 *  - Campo de busca interno (regra global: todo Select tem busca)
 *  - Botão X para limpar (regra global)
 *  - API compatível com <select>: onChange recebe { target: { value } }
 */
export function Select({
  label, error, hint, options, placeholder, wrapperClassName, className,
  value = '', onChange, disabled, required, name, id,
  searchable = true, clearable = true, searchPlaceholder = 'Buscar',
}: Props) {
  const opts = useMemo(
    () => options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o)),
    [options],
  )
  const selected = opts.find((o) => o.value === value)

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0, flipUp: false })

  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const searchInputRef = useRef<HTMLInputElement | null>(null)

  const filtered = useMemo(() => {
    // REGRA GLOBAL: busca ignora caixa E acento.
    const q = normalizeForMatch(query)
    if (!q) return opts
    return opts.filter((o) => normalizeForMatch(o.label).includes(q) || normalizeForMatch(o.value).includes(q))
  }, [opts, query])

  // Posicionamento via getBoundingClientRect — escapa de overflow.
  const recalc = () => {
    const btn = triggerRef.current
    if (!btn) return
    const rect = btn.getBoundingClientRect()
    const panelH = panelRef.current?.offsetHeight ?? Math.min(opts.length * 36 + 60, 320)
    const flipUp = window.innerHeight - rect.bottom < panelH + 12 && rect.top > panelH + 12
    setPos({
      top:  flipUp ? rect.top - panelH - 4 : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      flipUp,
    })
  }
  useLayoutEffect(() => {
    if (!open) return
    recalc()
    const id = requestAnimationFrame(recalc)
    // Foco no campo de busca ao abrir
    if (searchable) setTimeout(() => searchInputRef.current?.focus(), 0)
    return () => cancelAnimationFrame(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open) return
    // Scroll fora do painel reposiciona; scroll DENTRO do painel é ignorado
    // (do contrário, rolar a lista de opções com o mouse fecharia o select).
    const onScroll = (e: Event) => {
      const t = e.target as Node | null
      if (panelRef.current && t && panelRef.current.contains(t)) return
      recalc()
    }
    const onResize = () => recalc()
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (triggerRef.current?.contains(t)) return
      if (panelRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  function emit(v: string) {
    onChange?.({ target: { value: v } })
  }
  function pick(v: string) {
    emit(v)
    setOpen(false)
    setQuery('')
  }
  function clear(e: React.MouseEvent) {
    e.stopPropagation()
    emit('')
  }

  const hasError = Boolean(error)
  const hasValue = value !== '' && value !== null && value !== undefined
  const showClear = clearable && hasValue && !disabled

  return (
    <div className={cn('flex flex-col', wrapperClassName)}>
      {label && (
        <label className="text-xs font-medium text-gray-700 mb-1" htmlFor={id}>
          {label}
          {required && <span className="text-rose-500 ml-0.5">*</span>}
        </label>
      )}

      <button
        ref={triggerRef}
        type="button"
        id={id}
        name={name}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={cn(
          'group relative w-full inline-flex items-center bg-white rounded-md ring-1 px-3 py-2 text-sm transition cursor-pointer',
          'disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400',
          hasError ? 'ring-rose-300' : open ? 'ring-brand-500 ring-2' : 'ring-gray-200 hover:ring-gray-300',
          className,
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={cn('break-words flex-1 text-left', selected ? 'text-gray-900' : 'text-gray-400')}>
          {selected?.label ?? placeholder ?? 'Selecione'}
        </span>
        {showClear ? (
          <span
            role="button"
            tabIndex={-1}
            aria-label="Limpar"
            onClick={clear}
            className="cursor-pointer inline-flex items-center justify-center w-6 h-6 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition ml-1"
          >
            <X size={14} />
          </span>
        ) : (
          <ChevronDown size={14} className={cn('text-gray-400 transition-transform ml-1', open && 'rotate-180')} />
        )}
      </button>

      {(error || hint) && (
        <span className={cn('text-[11px] mt-1', hasError ? 'text-rose-600' : 'text-gray-500')}>
          {error || hint}
        </span>
      )}

      {open && createPortal(
        <div
          ref={panelRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, minWidth: 220, zIndex: 9999 }}
          className="bg-white ring-1 ring-gray-200 rounded-md py-1 max-h-[60vh] overflow-hidden flex flex-col"
        >
          {searchable && (
            <div className="px-2 py-2 border-b border-gray-100 sticky top-0 bg-white">
              <div className="relative flex items-center bg-gray-50 rounded ring-1 ring-gray-200 focus-within:ring-brand-500 focus-within:ring-2">
                <Search size={12} className="absolute left-2.5 text-gray-400 pointer-events-none" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  autoComplete="off"
                  className="w-full bg-transparent text-xs pl-7 pr-7 py-1.5 focus:outline-none placeholder:text-gray-400"
                />
                {query && (
                  <button
                    type="button"
                    tabIndex={-1}
                    aria-label="Limpar busca"
                    onClick={() => setQuery('')}
                    className="absolute right-1 cursor-pointer inline-flex items-center justify-center w-5 h-5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
                  >
                    <X size={11} />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Regra global: máximo 7 itens visíveis (36px cada ≈ 252px). */}
          <div role="listbox" className="overflow-y-auto flex-1 max-h-[252px]">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-xs text-gray-400 text-center">Nenhuma opção encontrada.</div>
            ) : filtered.map((o) => {
              const isSel = o.value === value
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={isSel}
                  onClick={() => pick(o.value)}
                  className={cn(
                    'cursor-pointer w-full flex items-center justify-between gap-2 px-3 py-2 text-[13px] text-left transition',
                    isSel ? 'bg-brand-50 text-brand-700' : 'text-gray-700 hover:bg-gray-50',
                  )}
                >
                  <span className="break-words">{o.label}</span>
                  {isSel && <Check size={13} className="text-brand-600 shrink-0" />}
                </button>
              )
            })}
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}
